import { HelpTooltip } from '../common/HelpTooltip';
import { formatEUR } from '../../utils/money';
import { cn } from '../../utils/cn';
import pt from '../../locales/pt';

/**
 * `.pen` → `ds/card/networth-formula` (`gOGdD`).
 *
 * Cartão de superfície (`$surface-card` / `$border` / `$radius-ds-lg`,
 * `padding $space-5`, `gap $space-4`): título «DE QUE É FEITO»
 * `$type-section` e a linha `cells` — três células com os operadores `−` e `=`
 * em `$font-mono` pelo meio. `TENS` e `DEVES` são células de largura fixa em
 * `$surface-muted`; `PATRIMÓNIO` ocupa o resto em `$sage-tint` / `$sage-border`.
 *
 * **Densidade por instância** (lida em cada frame de página, não do master):
 * | breakpoint | gap | TENS | DEVES | pad célula | pad destaque | valor |
 * |---|---|---|---|---|---|---|
 * | 375 (343px)  | 2 | 100 | 92 | 8/7 | 8/6 | `$type-body` |
 * | 768 (318px)  | 2 | 92  | 78 | 8/7 | 8/5 | `$type-body-sm` |
 * | 1280 (566px) | `$space-2` | 140 | 140 | `$space-2-5`/`$space-3` | idem | `$type-display-stat` / `$type-display-metric` |
 *
 * As classes `.type-*` são CSS simples (não utilities Tailwind) e não aceitam
 * prefixos `md:`/`lg:`; a densidade vem por isso do `layout` já calculado na
 * página, tal como antes. Os px acima são literais do `.pen`, não invenções.
 */
const DENSITY = {
  compact: {
    cellsGap: 'gap-[2px]',
    cellPad: 'px-[7px] py-2',
    highlightPad: 'px-[6px] py-2',
    haveWidth: 'w-[100px]',
    oweWidth: 'w-[92px]',
    value: 'text-type-body',
    highlightValue: 'text-type-body',
  },
  cozy: {
    cellsGap: 'gap-[2px]',
    cellPad: 'px-[7px] py-2',
    highlightPad: 'px-[5px] py-2',
    haveWidth: 'w-[92px]',
    oweWidth: 'w-[78px]',
    value: 'text-type-body-sm',
    highlightValue: 'text-type-body-sm',
  },
  full: {
    cellsGap: 'gap-space-2',
    cellPad: 'px-space-3 py-space-2-5',
    highlightPad: 'px-space-3 py-space-2-5',
    haveWidth: 'w-[140px]',
    oweWidth: 'w-[140px]',
    value: 'text-type-display-stat',
    highlightValue: 'text-type-display-metric',
  },
};

/** `.pen`: valores sempre `$font-display` 600 — só o `fontSize` muda. */
const VALUE_BASE = 'font-display font-semibold tracking-tight tnum';

function FormulaCell({ label, value, tooltip, highlight = false, widthClass, padClass, valueClass }) {
  return (
    <div
      className={cn(
        'flex min-w-0 flex-col gap-space-1 rounded-ds-md border',
        padClass,
        /* As larguras px são as do `.pen` por instância. `min-w-fit` em todas
           as células deixa o flexbox redistribuir a folga entre elas quando o
           valor formatado é mais largo do que o mock previa (o separador de
           milhares pt-PT é NBSP e não quebra), em vez de transbordar. */
        'min-w-fit',
        highlight
          ? 'flex-1 border-sage-border bg-sage-tint'
          : cn('border-border bg-muted', widthClass),
      )}
    >
      <p
        className={cn(
          'type-section flex min-w-0 items-center gap-space-1',
          highlight ? 'text-sage-on-tint opacity-90' : 'text-muted-foreground',
        )}
      >
        <span className="min-w-0 truncate">{label}</span>
        {tooltip ? <HelpTooltip appearance="plain" label={label} content={tooltip} /> : null}
      </p>
      <p
        className={cn(
          VALUE_BASE,
          valueClass,
          highlight ? 'text-sage-on-tint' : 'text-foreground',
        )}
      >
        {formatEUR(value)}
      </p>
    </div>
  );
}

function FormulaOperator({ symbol }) {
  return (
    <span
      className="shrink-0 font-mono text-type-display-stat font-normal text-muted-foreground opacity-60"
      aria-hidden
    >
      {symbol}
    </span>
  );
}

export function PatrimonioFormulaCard({
  assets,
  liabilities,
  netWorth,
  density = 'full',
  className,
}) {
  const d = DENSITY[density] || DENSITY.full;

  return (
    <section
      className={cn(
        'flex w-full min-w-0 flex-col gap-space-4 rounded-ds-lg border border-border bg-card p-space-5',
        className,
      )}
    >
      <p className="type-section text-muted-foreground">{pt.patrimonio.formula.title}</p>

      <div className={cn('flex min-w-0 items-center', d.cellsGap)}>
        <FormulaCell
          label={pt.patrimonio.formula.have}
          value={assets}
          tooltip={pt.patrimonio.formula.haveTooltip}
          widthClass={d.haveWidth}
          padClass={d.cellPad}
          valueClass={d.value}
        />
        <FormulaOperator symbol="−" />
        <FormulaCell
          label={pt.patrimonio.formula.owe}
          value={liabilities}
          tooltip={pt.patrimonio.formula.oweTooltip}
          widthClass={d.oweWidth}
          padClass={d.cellPad}
          valueClass={d.value}
        />
        <FormulaOperator symbol="=" />
        <FormulaCell
          label={pt.patrimonio.formula.netWorth}
          value={netWorth}
          tooltip={pt.patrimonio.formula.netWorthTooltip}
          padClass={d.highlightPad}
          valueClass={d.highlightValue}
          highlight
        />
      </div>
    </section>
  );
}

export default PatrimonioFormulaCard;
